import {debounceTime, Options, TableDataRequest} from '@fl/cmsch-fe-library';
import {debounce, isEmpty, isNumber} from 'lodash/fp';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {none, Opt, opt} from 'ts-opt';

import {BreederRoleManagement} from 'api/gen/BreederRoleManagement';
import {UserProfileBreederRoleFarms} from 'api/gen/UserProfileBreederRoleFarms';
import {breedersRowMapper} from 'app/admin/components/management-breeders-table/row-mapper';
import {useOurTranslation} from 'app/translations';
import {AsyncValidationSource} from 'utils/async-validation-source';
import {Errors} from 'utils/validator';

import {adminAction} from '../model';
import {
    managementBreedersFarmOptionsSelector,
    managementBreedersRoleOptionsSelector,
    managementBreedersUserOptionsSelector,
    simpleAdminSelector,
} from '../model/selector';
import {CreateOrEditOrNull} from '../types/create-or-edit-or-null';
import {FarmsSettings} from '../types/farms-settings';
import {ManagementBreedersTableType} from '../types/management-breeders';
import {SubjectManagementFacilityFormValues} from '../types/subject-management-facility-form-values';
import {UserProfileAndRole} from '../types/user-profile-and-role-ids';
import {createManagementBreedersValidate} from '../utils/create-management-breeders-validate';

const breederForm = 'breederForm';
const defaultUserProfileAndRoleIds: UserProfileAndRole = {
    role: null,
    userProfile: null,
};

interface UseManagementBreedersFormSetup {
    visibleModal: boolean;
    roleOptions: Options<number>;
    userProfileOptions: Options<number>;
    actionMode: CreateOrEditOrNull;
    breederForm: string;
    userProfileAndRoles: UserProfileAndRole;
    farmsSettings: FarmsSettings;
    breederId: Opt<number>;
    breedersOptions: Options<number>;
    emptyTableTooltip: string | undefined;
    breederName: string | undefined;
    breederDetail: Opt<UserProfileBreederRoleFarms>;
    hasAccessToAllFarms: boolean;
    handleColumnOpenCreateModal(roleId: string, roleName: string): void;
    handleRowOpenCreateModal(_id: number, record: ManagementBreedersTableType): void;
    handleOpenCreateModalWithoutLimitations(): void;
    handleOpenEditModal(): void;
    handleSubmitModal(): void;
    handleUserSearch(searchText: string): void;
    handleRoleSearch(searchText: string): void;
    handleCloseModal(): void;
    validateDebounced(source: AsyncValidationSource): void;
    removeUsersFromRole(columnRoleId: string): void;
    rowMapper(row: ManagementBreedersTableType): ManagementBreedersTableType;
    onTableFetch(tableDataRequest: Opt<TableDataRequest<ManagementBreedersTableType>>): void;
    changeValue(val: Opt<number>): void;
    searchBreeders(text: string): void;
    removeRolesForUser(_id: number, user: ManagementBreedersTableType): void;
    removeConfirmTitle(_id: number, user: ManagementBreedersTableType): string;
    validate(values: SubjectManagementFacilityFormValues): Errors<SubjectManagementFacilityFormValues>;
}

/** Builds the state and callbacks used by the breeder management screen. */
// eslint-disable-next-line max-lines-per-function
export const useManagementBreedersFormSetup = (): UseManagementBreedersFormSetup => {
    const dispatch = useDispatch();
    const {t} = useOurTranslation('admin');

    const breedersOptions = useSelector(simpleAdminSelector.managementBreedersOptions);
    const breedersFarms = useSelector(simpleAdminSelector.managementBreedersFarms);
    const [breederId, setBreederId] = useState<Opt<number>>(none);
    const breederDetail = useSelector(simpleAdminSelector.managementBreederDetail);
    const [hasAccessToAllFarms, setHasAccessToAllFarms] = useState(false);
    const breederName = useMemo(() =>
        breederId.isFull ? breedersOptions.find(x => x.value === breederId.orUndef())?.label : '',
    [breederId, breedersOptions]);
    const computeFarmsTooltip = useCallback((ids: Array<number>): string => breedersFarms
        .filter(farm => ids.includes(farm.id))
        .map(farm => farm.code)
        .join('\n')
    , [breedersFarms]);

    useEffect(() => {
        if (!isEmpty(breedersOptions) && breedersOptions.length === 1) {
            const firstOption = opt(breedersOptions).headIn().prop('value');
            setBreederId(opt(breedersOptions).headIn().prop('value'));
            dispatch(adminAction.getManagementBreedersTableData(opt({action: 'reset'}), firstOption));
        }
    }, [breedersOptions, dispatch]);

    const searchBreeders = useCallback((text: string) => {
        dispatch(adminAction.getBreedersOptions(text));
    }, [dispatch]);

    const changeValue = useCallback((val: Opt<number>) => {
        dispatch(adminAction.getManagementBreedersTableData(opt({action: 'reset'}), val));
        setBreederId(val);
    }, [dispatch]);

    const onTableFetch = useCallback((tableDataRequest: Opt<TableDataRequest<ManagementBreedersTableType>>) => {
        if (tableDataRequest.isFull) {
            dispatch(adminAction.getManagementBreedersTableData(tableDataRequest, breederId));
        }
    }, [breederId, dispatch]);

    const removeRolesForUser = useCallback((_id: number, user: ManagementBreedersTableType) => {
        if (breederId.isFull) {
            dispatch(adminAction.removeBreederRolesForUser(
                user.userProfileId,
                breederId.orCrash('breederId id not found for removing user roles'),
            ));
        }
    }, [dispatch, breederId]);

    const removeUsersFromRole = useCallback((columnRoleId: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);
        if (breederId.isFull && roleIdNarrowed.isFull) {
            dispatch(adminAction.removeUsersFromBreederRole(
                roleIdNarrowed.orCrash('Role Id is not number'),
                breederId.orCrash('Breeder id not found for removing user roles'),
            ));
        }
    }, [dispatch, breederId]);

    const removeConfirmTitle = useCallback((_id: number, user: ManagementBreedersTableType) =>
        t('confirmRemovingUserRoles', {user: user.username}), [t]);

    const emptyTableTooltip = breederId.isEmpty ? t('needSelectBreeder') : undefined;

    const [actionMode, setActionMode] = useState<CreateOrEditOrNull>(null);
    const handleOpenEditModal = useCallback(() => {
        setActionMode('edit');
        dispatch(adminAction.setManagementSubjectModalVisible(breederForm));
    }, [dispatch]);

    const handleDynamicCellClick = useCallback((role: BreederRoleManagement) => {
        dispatch(adminAction.getBreederDetail(role.userProfileBreederRoleId));
        setHasAccessToAllFarms(role.hasAccessToAllFarms);
        handleOpenEditModal();
    }, [dispatch, handleOpenEditModal]);

    const rowMapper = useCallback((row: ManagementBreedersTableType) =>
        breedersRowMapper(row, computeFarmsTooltip, handleDynamicCellClick),
    [computeFarmsTooltip, handleDynamicCellClick]);

    const [userProfileAndRoles, setUserProfileAndRoleIds] = useState<UserProfileAndRole>(defaultUserProfileAndRoleIds);

    const subjectManagementModalVisible = useSelector(simpleAdminSelector.subjectManagementModalVisible);
    const visibleModal = useMemo(() => subjectManagementModalVisible === breederForm, [subjectManagementModalVisible]);
    const roleOptions = useSelector(managementBreedersRoleOptionsSelector);
    const userProfileOptions = useSelector(managementBreedersUserOptionsSelector);
    const farmOptions = useSelector(managementBreedersFarmOptionsSelector);

    const farmsAreEmpty = useMemo(() => isEmpty(farmOptions), [farmOptions]);
    const validate = useMemo(() => createManagementBreedersValidate(!farmsAreEmpty), [farmsAreEmpty]);

    const handleSubmitModal = useCallback(() => {
        const safeBreederId = breederId.orCrash('Breeder id not found');

        dispatch(adminAction[(actionMode === 'create'
            ? 'createManagementBreeder'
            : 'updateBreederDetail'
        )](safeBreederId));
    },
    [actionMode, breederId, dispatch]);

    const handleOpenCreateModal = useCallback(() => {
        setActionMode('create');
        dispatch(adminAction.setManagementSubjectModalVisible(breederForm));
    }, [dispatch]);

    const handleColumnOpenCreateModal = useCallback((columnRoleId: string, roleName: string) => {
        const roleIdNarrowed = opt(columnRoleId).map(Number).narrow(isNumber);

        if (roleIdNarrowed.isFull) {
            setUserProfileAndRoleIds({
                role: {
                    id: roleIdNarrowed.orCrash('Role Id is not number'),
                    name: roleName,
                },
                userProfile: null,
            });
            handleOpenCreateModal();
        }
    }, [handleOpenCreateModal]);

    const handleRowOpenCreateModal = useCallback((_id: number, record: ManagementBreedersTableType) => {
        setUserProfileAndRoleIds({
            role: null,
            userProfile: {
                id: record.userProfileId,
                name: record.username,
            },
        });
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleOpenCreateModalWithoutLimitations = useCallback(() => {
        setUserProfileAndRoleIds(defaultUserProfileAndRoleIds);
        handleOpenCreateModal();
    }, [handleOpenCreateModal]);

    const handleCloseModal = useCallback(() => {
        setActionMode(null);
        dispatch(adminAction.setManagementSubjectModalVisible(null));
        dispatch(adminAction.setManagementBreedersUsers([]));
        dispatch(adminAction.setManagementBreedersRoles([]));
    }, [dispatch]);

    const handleUserSearch = useCallback((searchText: string): void => {
        dispatch(adminAction.searchManagementBreedersUsers(searchText));
    }, [dispatch]);

    const validateData = useCallback((source: AsyncValidationSource) => {
        dispatch(adminAction.validateManagementBreeder(source));
    }, [dispatch]);

    const handleRoleSearch = useCallback((searchText: string): void => {
        dispatch(adminAction.searchManagementBreedersRoles(searchText));
    }, [dispatch]);

    const validateDebounced = useMemo(() => debounce(debounceTime)(validateData), [validateData]);

    const farmsSettings: FarmsSettings = useMemo(() => ({
        allFarmTitle: t('allFarms'),
        farmOptions,
        farmsAreEmpty,
        farmTitle: t('farms'),
        isRequired: !farmsAreEmpty,
    }), [farmsAreEmpty, farmOptions, t]);

    return {
        handleSubmitModal,
        handleUserSearch,
        handleRoleSearch,
        handleColumnOpenCreateModal,
        handleRowOpenCreateModal,
        handleOpenCreateModalWithoutLimitations,
        handleCloseModal,
        visibleModal,
        roleOptions,
        userProfileOptions,
        actionMode,
        breederForm,
        handleOpenEditModal,
        validate,
        userProfileAndRoles,
        farmsSettings,
        breedersOptions,
        changeValue,
        searchBreeders,
        breederId,
        removeRolesForUser,
        removeConfirmTitle,
        removeUsersFromRole,
        onTableFetch,
        rowMapper,
        emptyTableTooltip,
        breederName,
        breederDetail,
        hasAccessToAllFarms,
        validateDebounced,
    };
};
